import type { MatchedLocation } from '../lib/api.js';

/**
 * THE PARTS OF A LOCATION CARD THAT MUST NOT DRIFT.
 *
 * The Locations tab and the setup flow's Explore step show the same matched
 * location, and the acceptance criterion says they show the same record. The
 * two cards had already drifted on the cheapest thing to drift: the meta
 * line read "From 1200/day" on one and "· 1200" on the other. The photo, its
 * shortlisted ribbon and the meta line are built here, once; each surface
 * keeps its own layout and its own actions around them.
 */

/** "HOTEL · Varna, BG · From 1200/day" — one spelling on every surface. */
export function locationMetaLine(
  location: Pick<MatchedLocation, 'typeCode' | 'city' | 'countryIso' | 'priceOnRequest' | 'rateAmount'>,
): string {
  return [
    location.typeCode,
    [location.city, location.countryIso].filter(Boolean).join(', ') || null,
    location.priceOnRequest
      ? 'Price on request'
      : location.rateAmount === null
        ? null
        : `From ${String(location.rateAmount)}/day`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The photograph, with the shortlisted ribbon ON it — where somebody
 * scanning a long list is actually looking. `row` is the compact 4:3 picture
 * of a result row (the Locations tab and Explore since 2026-10-30); `hero` and
 * `thumb` are the larger and the older small one.
 */
export function LocationPhoto({
  location,
  shortlisted,
  variant,
  testIdPrefix,
}: {
  location: Pick<MatchedLocation, 'imageUrl' | 'sourceId'>;
  shortlisted: boolean;
  variant: 'hero' | 'thumb' | 'row';
  testIdPrefix: string;
}) {
  if (location.imageUrl === null) return null;

  return (
    <span
      style={{
        position: 'relative',
        display: 'block',
        ...(variant === 'hero' ? { marginBottom: 12 } : variant === 'row' ? {} : { width: 120, flex: 'none' }),
      }}
    >
      {shortlisted ? (
        <span
          className="stag"
          data-testid={`photo-shortlisted-${String(location.sourceId)}`}
          style={{
            position: 'absolute',
            top: variant === 'hero' ? 10 : 6,
            left: variant === 'hero' ? 10 : 6,
            ...(variant === 'row' ? { fontSize: 10, padding: '3px 6px' } : {}),
            background: 'var(--ink)',
            color: '#fff',
            zIndex: 1,
          }}
        >
          SHORTLISTED
        </span>
      ) : null}
      {/* Decorative alt: the name is beside the picture, and a screen reader
          repeating it is noise. Lazy, because a matched list is long. */}
      <img
        src={location.imageUrl}
        alt=""
        loading="lazy"
        data-testid={`${testIdPrefix}-photo-${String(location.sourceId)}`}
        style={{
          width: '100%',
          aspectRatio: variant === 'hero' ? '16 / 10' : variant === 'row' ? '4 / 3' : '10 / 7',
          objectFit: 'cover',
          display: 'block',
          background: 'var(--color-neutral-100)',
        }}
      />
    </span>
  );
}
